/**
 * configLoader.js
 * -----------------
 * Finds versioned config files from feature folders. Supports JSON and JS.
 * Designed for modern bundlers:
 *   - Webpack: `require.context`
 *
 * Each config module must export default:
 * {
 *   version: "1.1.1",                 // string "store.object.data"
 *   stores: { [storeName]: dexieDef },
 *   model?: [{storeObject:{}}, {dataObject:{}}] | {storeObject?:{}, dataObject?:{}},
 *   data?: Array<object>               // initial data or patch data
 * }
 */

/**
 * Normalizes a loaded module or JSON object into a canonical config shape.
 * - Accepts ESM default exports.
 * - Converts array-based `model` (e.g., [{storeObject:{}},{dataObject:{}}]) to an object.
 *
 * @param {*} raw - Raw module or JSON contents.
 * @param {string} filePath - Source path for diagnostics.
 * @throws {Error} If required keys are missing.
 * @returns {{filePath:string, version:string, stores:Object<string,string>, model?:Object, data:Object[]}}
 */
// function normalizeConfig(raw, filePath) {
//   const c = raw && raw.default ? raw.default : raw; // handle ESM default
//   if (!c || !c.version || !c.stores) {
//     console.error(`Invalid config at ${filePath}: missing version/stores`);
//     throw new Error(`Invalid config at ${filePath}: missing version/stores`);
//   }
//   let model = undefined;
//   if (Array.isArray(c.model)) {
//     model = {};
//     for (const bucket of c.model) Object.assign(model, bucket);
//   } else if (c.model && typeof c.model === "object") {
//     model = c.model;
//   }
//   return {
//     filePath,
//     fileVersion: extractVersionFromFilename(filePath), // (may be null)
//     version: String(c.version),
//     stores: { ...c.stores },
//     model, // may be undefined
//     data: Array.isArray(c.data) ? c.data.slice() : [],
//   };
// }
export function normalizeConfig(rawModule, filePath) {
  const c = rawModule && rawModule.default ? rawModule.default : rawModule;

  // ---- Basic structural validation ----
  if (!c || !c.version || !c.stores) {
    throw new Error(`Invalid config at ${filePath}: missing version/stores`);
  }

  // ---- Expect exactly ONE store in this config ----
  const storeNames = Object.keys(c.stores);
  if (storeNames.length !== 1) {
    throw new Error(
      `Config file ${filePath} must define exactly 1 store (found ${storeNames.length}).`
    );
  }

  const storeFromConfig = storeNames[0];

  // ---- Extract store name from filename ----
  const storeFromFile = extractStoreFromFilename(filePath);
  if (!storeFromFile) {
    throw new Error(
      `Config filename must match pattern: config-<store>-<version>.js. Invalid file: ${filePath}`
    );
  }

  // ---- Enforce store name consistency ----
  if (storeFromFile !== storeFromConfig) {
    throw new Error(
      `Store mismatch: filename store "${storeFromFile}" does not match config store "${storeFromConfig}" in ${filePath}`
    );
  }

  // ---- Check version match (warn only) ----
  const fileVersion = extractVersionFromFilename(filePath);
  if (fileVersion && fileVersion !== String(c.version)) {
    console.warn(
      `Version mismatch in ${filePath}: filename=${fileVersion}, config=${c.version}`
    );
  }

  // ---- Normalize model shape (original behavior preserved) ----
  let model = undefined;
  if (Array.isArray(c.model)) {
    model = {};
    for (const bucket of c.model) Object.assign(model, bucket);
  } else if (c.model && typeof c.model === "object") {
    model = c.model;
  }

  // ---- Final normalized object ----
  return {
    filePath,
    fileVersion,
    version: String(c.version),
    type: String(c.type),
    changeLog: Array.isArray(c.changeLog) ? c.changeLog.slice() : [],

    //changeDescription is a public facing message to present to user on update
    changeDescription: String(c.changeDescription),

    // ✅ Single store map preserved even though there's only one
    stores: { ...c.stores },

    // ✅ May be undefined, or now merged single-level object
    model,

    // defines policies when populating data (e.g. id should generate cuid2)
    dataPolicies: { ...c.dataPolicies },

    // ✅ Always shallow-cloned
    data: Array.isArray(c.data) ? c.data.slice() : [],

    // Convenient store name (optional, but nice to have)
    store: storeFromConfig,

    // optional hooks
    hooks: c.hooks || undefined, // pass-through hooks
  };
}

/**
 * Extracts a semantic version string (major.minor.patch) from a config filename.
 *
 * Searches for a pattern like "config_<version>.js" or "config_<version>.json"
 * (case-insensitive) and returns the captured version portion (e.g. "1.2.3").
 * If the input is falsy or no matching version is found, the function returns null.
 *
 * @param {string} filePath - The file path or filename to inspect (e.g. "path/to/config_1.2.3.js").
 * @returns {string|null} The extracted version string if present (e.g. "1.2.3"), otherwise null.
 *
 * @example
 * // returns "1.2.3"
 * extractFileVersion("config_1.2.3.js");
 *
 * @example
 * // returns "10.0.0"
 * extractFileVersion("/dist/config_10.0.0.json");
 *
 * @example
 * // returns null
 * extractFileVersion("README.md");
 */
// export function extractFileVersion(filePath) {
//   const m = /config_(\d+\.\d+\.\d+)\.(?:js|json)$/i.exec(
//     String(filePath || "")
//   );
//   return m ? m[1] : null;
// }

/**
 * Extract <storeName> from "config-<store>-<A.B.C>.js"
 */
export function extractStoreFromFilename(path) {
  const m = /config-([^/]+)-\d+\.\d+\.\d+\.(js|json)$/i.exec(path);
  return m ? m[1] : null;
}

/**
 * Extract version <A.B.C> from filename
 */
export function extractVersionFromFilename(path) {
  const m = /\d+\.\d+\.\d+/.exec(path);
  return m ? m[0] : null;
}

/**
 * Validate that a value represents a dot-separated triplet of integers (e.g. "1.2.3").
 *
 * The function performs the following steps:
 * 1. Coerces the input into a string by using `String(v || "")`. This means
 *    all falsy values (undefined, null, false, 0, NaN, "") become the empty
 *    string and will therefore fail validation.
 * 2. Tests the resulting string against the regular expression /^\d+\.\d+\.\d+$/.
 *
 * The regular expression breakdown:
 * - ^ and $: anchors that require the entire string to match (no extra
 *   characters before or after).
 * - \d+: one or more ASCII digits (0-9) for each component.
 * - \.: a literal dot character separating components.
 * - The pattern enforces exactly three numeric components separated by two dots.
 *
 * Important notes and edge cases:
 * - Leading zeros are allowed (e.g. "01.002.3" is considered valid).
 * - Negative numbers, signs, spaces, alphabetic characters, and decimals within
 *   components are not allowed (e.g. "-1.2.3", "1. 2.3", "1.2.3a" are invalid).
 * - Numeric inputs like 1.2 or 123 will not match because they do not produce
 *   the required "digits.digits.digits" format when stringified.
 * - Objects with a custom toString() may match if their string representation
 *   exactly equals the triplet pattern.
 * - This function only checks syntactic form; it does not enforce any semantic
 *   ranges (e.g. component values are not checked to be <= 255 or similar).
 *
 * @param {*} v - The value to validate. Can be of any type; it will be coerced to a string
 *                via `String(v || "")` before testing.
 * @returns {boolean} True if the coerced string exactly matches the pattern "digits.digits.digits",
 *                    otherwise false.
 *
 * @example
 * isValidTriplet("1.2.3");      // true
 * isValidTriplet("01.002.3");   // true
 * isValidTriplet("1.2");        // false
 * isValidTriplet("1.2.3.4");    // false
 * isValidTriplet("a.b.c");      // false
 * isValidTriplet(null);         // false
 * isValidTriplet(0);            // false
 * isValidTriplet({ toString: () => "9.9.9" }); // true
 */
export function isValidTriplet(v) {
  return /^\d+\.\d+\.\d+$/.test(String(v || ""));
}

/**
 * Parses a semantic triplet "A.B.C".
 * @param {string|number} v - Version string or numeric-ish.
 * @returns {{major:number, minor:number, patch:number}}
 */
export function parseTriplet(v) {
  const [A, B, C] = String(v)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  return { major: A, minor: B, patch: C };
}

/**
 * Compares two triplets.
 * @param {string|{major:number,minor:number,patch:number}} a
 * @param {string|{major:number,minor:number,patch:number}} b
 * @returns {number} -1 if a<b, 0 if equal, +1 if a>b
 */
export function cmpTriplet(a, b) {
  const A = typeof a === "string" ? parseTriplet(a) : a;
  const B = typeof b === "string" ? parseTriplet(b) : b;
  if (A.major !== B.major) return A.major - B.major;
  if (A.minor !== B.minor) return A.minor - B.minor;
  return A.patch - B.patch;
}

/**
 * Loads all config files using Webpack `require.context`.
 *
 * @example
 *   const list = loadConfigsWithRequireContext();
 *
 * @returns {Array<{filePath:string, version:string, stores:Object<string,string>, model?:Object, data:Object[]}>}
 */
// export function loadConfigsWithRequireContext() {
//   const ctx = require.context(
//     "../../features",
//     true,
//     /\/schema\/config_.*\.(json|js)$/
//   );
//   return ctx.keys().map((k) => normalizeConfig(ctx(k), k));
// }
// src/db/schema/config-loader.js

export function collectConfigs() {
  console.debug("collecting configs");

  // 1) Make sure this path points EXACTLY at your features root on disk.
  //    Adjust the relative path as needed given where THIS file lives.
  //    Example: if this file is at src/db/schema/config-loader.js,
  //    and your features are at src/features, then:
  const ctx = require.context(
    "../../features", // <-- exact folder
    true, // recurse
    /^\.\/.*\/schema\/config-[^/]+-\d+\.\d+\.\d+\.(js|json)$/i // <-- anchor keys to "./"
  );

  const out = [];
  const seen = new Set(); // dedupe by "store@version" OR by ctx.resolve(key)

  ctx.keys().forEach((key) => {
    // Optional: dedupe by fully resolved module id (most precise)
    const resolvedId = ctx.resolve(key);

    if (seen.has(resolvedId)) {
      // If you want to SEE why it duped, uncomment:
      // console.warn("Duplicate config detected (same module):", key);
      return;
    }

    const mod = ctx(key);
    const normalized = normalizeConfig(mod, key);

    // Extra dedupe layer by semantic identity (store@version)
    const identity = `${
      normalized.store || Object.keys(normalized.stores)[0]
    }@${normalized.version}`;
    if (seen.has(identity)) {
      // If different virtual paths map to the same store/version, prefer first
      // and warn once so you can fix the alias/root later.
      console.warn(`Duplicate config for ${identity} ignored: ${key}`);
      return;
    }

    // mark both ids as seen to avoid later duplicates by either method
    seen.add(resolvedId);
    seen.add(identity);

    out.push(normalized);
  });

  return out;
}

// previous
// export function collectConfigs() {
//   console.debug("collecting configs");
//   // Matches: config-<store>-1.2.3.js or .json
//   const ctx = require.context(
//     "../../features", // scans entire src tree (adjust path if needed)
//     true,
//     /\/schema\/config-[^/]+-\d+\.\d+\.\d+\.(js|json)$/i
//   );

//   const out = [];
//   ctx.keys().forEach((key) => {
//     const mod = ctx(key);
//     const normalized = normalizeConfig(mod, key);
//     out.push(normalized);
//   });

//   return out;
// }

/**
 * loadConfigByVersion(version, opts?)
 * -----------------------------------
 * Loads and normalizes a *single* config module (JS or JSON)
 * that matches the given semantic version (e.g., "1.1.1").
 *
 * ✅ Uses the same `normalizeConfig()` function already in this file
 *    so the returned object is identical to those loaded by
 *    `loadFeatureConfigsWithRequireContext()` or `loadFeatureConfigsWithGlob()`.
 *
 * Matching strategy:
 * - Scans the same Webpack context: `/features/xx/schema/config_*.{js,json}`
 * - Matches filename: `config_<version>.(js|json)`
 * - Optionally narrows by folder path (exact or substring)
 * - Returns a single normalized config object
 *
 * @param {string} version  Semantic version like "1.1.1"
 * @param {Object} [opts]
 * @param {string} [opts.folderExact]     Match a specific folder substring exactly
 * @param {string} [opts.folderIncludes]  Match if the path contains this substring
 * @param {boolean} [opts.preferJs=true]  Prefer `.js` when both `.js` and `.json` exist
 * @returns {Object} normalized config object
 * @throws {Error} if no match or multiple ambiguous matches
 *
 * @example
 * const cfg = loadConfigByVersion("1.1.1", { folderExact: "features/settings/schema" });
 */
export function loadConfigByVersion(version, opts = {}) {
  const { folderExact, folderIncludes, preferJs = true } = opts;

  // Validate version format
  const vRe = /^\d+\.\d+\.\d+$/;
  if (!vRe.test(String(version))) {
    throw new Error(
      `loadConfigByVersion: invalid version "${version}" (expected A.B.C format)`
    );
  }

  // Use the same Webpack context as the bulk loader
  const ctx = require.context(
    "../../features",
    true,
    /\/schema\/config_\d+\.\d+\.\d+\.(json|js)$/
  );

  // Find matching config files for the given version
  let candidates = ctx
    .keys()
    .filter(
      (k) =>
        k.endsWith(`/config_${version}.js`) ||
        k.endsWith(`/config_${version}.json`)
    );

  // Optionally narrow by folder path
  if (folderExact) {
    const needle = normalizePathFragment(folderExact);
    candidates = candidates.filter((k) =>
      normalizePathFragment(k).includes(needle)
    );
  } else if (folderIncludes) {
    const needle = normalizePathFragment(folderIncludes);
    candidates = candidates.filter((k) =>
      normalizePathFragment(k).includes(needle)
    );
  }

  if (candidates.length === 0) {
    throw new Error(
      `loadConfigByVersion: no config found for version ${version}` +
        (folderExact
          ? ` in "${folderExact}"`
          : folderIncludes
          ? ` (~"${folderIncludes}")`
          : "")
    );
  }

  // Prefer .js or .json if both exist
  if (candidates.length > 1) {
    const js = candidates.filter((k) => k.endsWith(".js"));
    const json = candidates.filter((k) => k.endsWith(".json"));
    if (preferJs && js.length === 1) candidates = js;
    else if (!preferJs && json.length === 1) candidates = json;
  }

  if (candidates.length !== 1) {
    const list = candidates.map((k) => ` - ${k}`).join("\n");
    throw new Error(
      `loadConfigByVersion: multiple candidates for ${version}:\n${list}\n` +
        `Provide { folderExact: ".../schema" } or { folderIncludes: "settings/schema" } to disambiguate.`
    );
  }

  // Load and normalize using the existing normalizeConfig()
  const filePath = candidates[0];
  const rawModule = ctx(filePath);
  const normalized = normalizeConfig(rawModule, filePath);
  return normalized;
}

/**
 * normalizePathFragment(p)
 * Helper to normalize paths for substring matching.
 */
function normalizePathFragment(p) {
  return String(p).replace(/\\/g, "/").toLowerCase();
}
